import { API_BASE, PORTAL_ID } from './api';
import { portalFetchInit } from '../../admin-shared/portalApiAuth';

function fetchInit(extra = {}) {
  return portalFetchInit(PORTAL_ID, {
    headers: { 'Content-Type': 'application/json', ...(extra.headers || {}) },
    ...extra,
  });
}

async function parseJson(res) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || data.message || `Request failed (${res.status})`);
  }
  return data;
}

export async function listFranchisePriceProducts({ includeInactive = true } = {}) {
  const qs = includeInactive ? '?include_inactive=1' : '';
  const res = await fetch(`${API_BASE}/franchise-price-products${qs}`, fetchInit());
  const data = await parseJson(res);
  return Array.isArray(data.items) ? data.items : [];
}

export async function createFranchisePriceProduct(payload) {
  const res = await fetch(`${API_BASE}/franchise-price-products`, fetchInit({
    method: 'POST',
    body: JSON.stringify(payload || {}),
  }));
  const data = await parseJson(res);
  return data.item;
}

export async function updateFranchisePriceProduct(id, payload) {
  const res = await fetch(`${API_BASE}/franchise-price-products/${encodeURIComponent(id)}`, fetchInit({
    method: 'PUT',
    body: JSON.stringify(payload || {}),
  }));
  const data = await parseJson(res);
  return data.item;
}

export async function setProductPublishStatus(productId, publishStatus) {
  const res = await fetch(`${API_BASE}/franchise-price-products/publish-status`, fetchInit({
    method: 'POST',
    body: JSON.stringify({ product_id: productId, publish_status: publishStatus }),
  }));
  const data = await parseJson(res);
  return data.item;
}

export async function deleteFranchisePriceProduct(id, { hard = true } = {}) {
  const qs = hard ? '?hard=1' : '';
  const res = await fetch(
    `${API_BASE}/franchise-price-products/${encodeURIComponent(id)}${qs}`,
    fetchInit({ method: 'DELETE' }),
  );
  return parseJson(res);
}

export async function importPreviewFranchisePriceProducts(url, { productDomain = 'skin' } = {}) {
  const res = await fetch(`${API_BASE}/franchise-price-products/import-preview`, fetchInit({
    method: 'POST',
    body: JSON.stringify({ url, product_domain: productDomain }),
  }));
  return parseJson(res);
}

export async function importCommitFranchisePriceProducts({
  items,
  sourceUrl,
  replaceAll = false,
  productDomain = 'skin',
} = {}) {
  const res = await fetch(`${API_BASE}/franchise-price-products/import-commit`, fetchInit({
    method: 'POST',
    body: JSON.stringify({
      items: (items || []).map((item) => ({
        ...item,
        product_domain: item.product_domain || productDomain,
      })),
      source_url: sourceUrl,
      replace_all: Boolean(replaceAll),
      product_domain: productDomain,
    }),
  }));
  return parseJson(res);
}

/** Upload photos of one product; Gemma extracts fields for the selected Skin/Hair type. */
export async function extractFranchisePriceProductFromImages(files, { productDomain = 'skin' } = {}) {
  const form = new FormData();
  form.append('product_domain', productDomain || 'skin');
  const list = Array.isArray(files) ? files : [files];
  list.filter(Boolean).forEach((file) => {
    form.append('images', file, file.name || 'product.jpg');
  });
  const res = await fetch(
    `${API_BASE}/franchise-price-products/extract-from-images`,
    portalFetchInit(PORTAL_ID, {
      method: 'POST',
      body: form,
    }),
  );
  return parseJson(res);
}

/** Partner desk: mint phone→desk product photo capture session. */
export async function createProductPhotoCaptureSession({
  productDomain = 'skin',
  branding,
} = {}) {
  const body = { product_domain: productDomain || 'skin' };
  if (branding && typeof branding === 'object') body.branding = branding;
  const res = await fetch(
    `${API_BASE}/franchise-price-products/photo-capture/sessions`,
    fetchInit({ method: 'POST', body: JSON.stringify(body) }),
  );
  const data = await parseJson(res);
  const token = typeof data.token === 'string' ? data.token.trim() : '';
  if (!token) throw new Error('Capture session token missing');
  return {
    token,
    productDomain: data.product_domain || productDomain || 'skin',
    pagePath: data.page_path || `/naturals/workspace/product-photo-capture/${token}`,
    expiresInSeconds: Number(data.expires_in_seconds) || 600,
  };
}

export function buildProductPhotoCapturePageUrl(pagePath) {
  const path = String(pagePath || '').startsWith('/')
    ? String(pagePath)
    : `/${String(pagePath || '').replace(/^\/+/, '')}`;
  return new URL(path, window.location.origin).toString();
}

/** Public: phone checks session status. */
export async function getProductPhotoCaptureStatus(token) {
  const res = await fetch(
    `${API_BASE}/franchise-price-products/photo-capture/${encodeURIComponent(token)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
  );
  const data = await res.json().catch(() => ({}));
  if (res.status === 404) {
    return {
      ok: false,
      status: data.status || 'expired',
      branding: data.branding || null,
      productDomain: data.product_domain || 'skin',
    };
  }
  return {
    ok: Boolean(data.ok ?? data.success),
    status: data.status || 'expired',
    expiresInSeconds: data.expires_in_seconds,
    imageCount: Number(data.image_count) || 0,
    maxImages: Number(data.max_images) || 6,
    productDomain: data.product_domain || 'skin',
    branding: data.branding && typeof data.branding === 'object' ? data.branding : null,
  };
}

/** Public: phone posts one or more captured/uploaded images. */
export async function postProductPhotoCaptureImages(token, imageDataUrls) {
  const images = (Array.isArray(imageDataUrls) ? imageDataUrls : [imageDataUrls]).filter(Boolean);
  const res = await fetch(
    `${API_BASE}/franchise-price-products/photo-capture/${encodeURIComponent(token)}/images`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ images }),
    },
  );
  return parseJson(res);
}

/** Public: phone marks session ready for desk claim. */
export async function completeProductPhotoCapture(token) {
  const res = await fetch(
    `${API_BASE}/franchise-price-products/photo-capture/${encodeURIComponent(token)}/complete`,
    {
      method: 'POST',
      headers: { Accept: 'application/json' },
    },
  );
  return parseJson(res);
}

/** Partner desk: poll/claim uploaded images (one-shot when ready). */
export async function claimProductPhotoCapture(token) {
  const res = await fetch(
    `${API_BASE}/franchise-price-products/photo-capture/${encodeURIComponent(token)}/claim`,
    fetchInit({ method: 'POST', body: JSON.stringify({}) }),
  );
  const data = await parseJson(res);
  if (data.status === 'waiting') {
    return { status: 'waiting', images: [], productDomain: data.product_domain || 'skin' };
  }
  return {
    status: data.status || 'consumed',
    images: Array.isArray(data.images) ? data.images : [],
    imageCount: Number(data.image_count) || 0,
    productDomain: data.product_domain || 'skin',
  };
}

/** Convert claim data-URL images into File objects for extract-from-images. */
export function dataUrlsToFiles(imagePayloads) {
  const list = Array.isArray(imagePayloads) ? imagePayloads : [];
  return list
    .map((item, index) => {
      const dataUrl = typeof item === 'string' ? item : item?.image;
      if (!dataUrl || typeof dataUrl !== 'string' || !dataUrl.includes(',')) return null;
      const [header, b64] = dataUrl.split(',', 2);
      const mimeMatch = /data:([^;]+)/.exec(header || '');
      const mime = (mimeMatch && mimeMatch[1]) || item?.content_type || 'image/jpeg';
      const binary = atob(b64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      const ext = mime.includes('png') ? 'png' : mime.includes('webp') ? 'webp' : 'jpg';
      const name = item?.filename || `product-${index + 1}.${ext}`;
      return new File([bytes], name, { type: mime });
    })
    .filter(Boolean);
}
